import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useIsClient, useLockBodyScroll } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { checkoutMutations, customerQueries, storeQueries } from '@as-react/common-queries';
import { Cookie, isLocationSearchResult, LocationSearchType, StoreFinderType, } from '@as-react/common-types';
import { CookieUtil } from '@as-react/common-utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Banner, BottomSheet, Box, ButtonAlpha, Grid, Sidebar, TextAlpha, useMediaQueryMatchesOnMobile, } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import OsmAPI from '../../../api/open-street-map/OsmApi';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { SEARCH_STORE_STORAGE_KEY, SELECTED_STORE_STORAGE_KEY } from '../../../constants/StorageKeysConstants';
import { LOCATION_TYPE } from '../../../constants/StoreFinderConstants';
import { makeGetBasketMappedProductsState } from '../../../redux/basket/selectors/BasketSelector';
import { makeGetSpecialtyType } from '../../../redux/product/selectors/ProductSelector';
import { makeGetIsLoggedIn } from '../../../redux/session/selectors/SessionSelector';
import LocalStorageUtil from '../../../util/LocalStorageUtil';
import StoreUtil from '../../../util/StoreUtil';
import ZendeskWidgetUtil from '../../../util/ZendeskWidgetUtil';
import StoreFinderGeneral from './store-finder-general/StoreFinderGeneral';
import { StoreList } from './store-list/StoreList';
import { StoreListLoader } from './store-list-loader/StoreListLoader';
import { useUserLocation } from './utils/useUserLocation';
const initLocation = {
    lat: '',
    lng: '',
};
const SIDEBAR_WIDTH = '512px';
const initLocationSearchResult = {
    location: initLocation,
    locationName: '',
    type: LocationSearchType.DEFAULT,
    state: 'INIT',
};
export const StoreFinderPopup = ({ handleClosePopup, isOpen, type = StoreFinderType.CLICK_AND_COLLECT, disabledCTA = false, textCTA, title, storeSourceExcluded, onStoreSelect, onSearch, storesQueryResult, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const isMobile = useMediaQueryMatchesOnMobile();
    const isClient = useIsClient();
    const isLoggedIn = useSelector(makeGetIsLoggedIn());
    const specialtyType = useSelector(makeGetSpecialtyType());
    const products = useSelector(makeGetBasketMappedProductsState());
    const { defaultRequestParams, servicesEndpoint, countryCodes, defaultCountryCode, isShopLanding, businessUnitId, pageInfo: { isPDPPage }, } = useAemConfig();
    const isSpecialtyTypeProducts = !!specialtyType || (!isPDPPage && !!products.find(product => product.specialtyStoreProduct));
    const isSSEProducts = !!storeSourceExcluded || (!isPDPPage && !!products.find(product => product.sseProduct));
    const [locationSearchResult, setLocationSearchResult] = useState(initLocationSearchResult);
    const [isShowStock, setIsShowStock] = useState(isSpecialtyTypeProducts && isPDPPage);
    const [userSelectedStore, setUserSelectedStore] = useState();
    const [isStoreFinderPopupCTAOpen, setIsStoreFinderPopupCTAOpen] = useState(false);
    const [isLocationBlocked, setIsLocationBlocked] = useState(false);
    const typeBasketCheckoutOrCC = type === StoreFinderType.CLICK_AND_COLLECT || type === StoreFinderType.BASKET || type === StoreFinderType.CHECKOUT;
    const { getProfile } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const { getStoreInformation } = storeQueries({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { postClickAndCollectStoreId } = checkoutMutations({ servicesEndpoint, defaultRequestParams });
    const { mutateAsync: setClickAndCollectStoreId } = useMutation(postClickAndCollectStoreId);
    const { data: profile } = useQuery(Object.assign(Object.assign({}, getProfile()), { enabled: Boolean(isLoggedIn) }));
    const { data: userLocation, error: userLocationError, status: userLocationStatus, } = useUserLocation({
        shouldCheckLocation: isOpen,
    });
    const { data: stores, isLoading: storesIsLoading, error: storesError } = storesQueryResult;
    const { data: storeInformation } = useQuery(getStoreInformation({
        businessUnitId,
        storeId: (userSelectedStore || {}).storeId,
        weeks: 2,
    }, { enabled: !!(userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId) }));
    useEffect(() => {
        if (!userLocationError) {
            return;
        }
        const { code } = userLocationError;
        if (Number(code) === GeolocationPositionError.PERMISSION_DENIED) {
            setLocationSearchResult(Object.assign(Object.assign({}, initLocationSearchResult), { state: 'SET' }));
            setIsLocationBlocked(true);
        }
        onSearch({
            locationSearch: Object.assign(Object.assign({}, initLocationSearchResult), { state: 'SET' }),
            isShowStock,
            sortBy: getSortBy(),
            selectedStoreId: userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId,
        });
    }, [userLocationError]);
    useEffect(() => {
        if (userLocation &&
            isLocationSearchResult(userLocation) &&
            locationSearchResult.type !== LocationSearchType.CUSTOM_SEARCH) {
            setLocationSearchResult(userLocation);
            onSearch({
                locationSearch: userLocation,
                isShowStock,
                sortBy: getSortBy(),
                selectedStoreId: userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId,
            });
        }
    }, [userLocation]);
    useEffect(() => {
        var _a;
        if (!isOpen)
            return;
        const selectedInStorageStoreId = (_a = LocalStorageUtil.getJsonItem(SELECTED_STORE_STORAGE_KEY)) === null || _a === void 0 ? void 0 : _a.storeId;
        const selectedStore = stores === null || stores === void 0 ? void 0 : stores.find(store => store.storeId === selectedInStorageStoreId);
        if (selectedInStorageStoreId && selectedStore) {
            handleStoreSelect(selectedStore);
        }
        else {
            setUserSelectedStore(undefined);
        }
    }, [stores, isOpen]);
    useEffect(() => {
        if (isOpen) {
            setIsShowStock(isSpecialtyTypeProducts && isPDPPage);
            setIsStoreFinderPopupCTAOpen(isOpen);
            ZendeskWidgetUtil.updateZIndex(1);
        }
    }, [specialtyType, products, isOpen]);
    useEffect(() => {
        CookieUtil.addChangeListener(Cookie.BROWSER_GEOLOCATION, parseCookieLocation);
        return () => {
            CookieUtil.removeChangeListener(Cookie.BROWSER_GEOLOCATION, parseCookieLocation);
        };
    }, []);
    const onPopoverClose = () => {
        setIsShowStock(isSpecialtyTypeProducts);
        handleClosePopup();
        setUserSelectedStore(undefined);
    };
    const parseCookieLocation = (value) => {
        let tmpLocationSearchResult = initLocationSearchResult;
        if (value) {
            const parsed = JSON.parse(decodeURIComponent(value));
            if (isLocationSearchResult(parsed)) {
                tmpLocationSearchResult = parsed;
            }
            else {
                tmpLocationSearchResult.location = parsed;
            }
        }
        setLocationSearchResult(tmpLocationSearchResult);
    };
    const getSortBy = () => {
        var _a;
        if (userLocationError) {
            return 'storeName';
        }
        if (locationSearchResult.type === LocationSearchType.CUSTOM_SEARCH || isShopLanding) {
            return 'distance';
        }
        if (!isLoggedIn) {
            return userLocationError ? 'storeName' : 'distance';
        }
        else {
            return ((_a = profile === null || profile === void 0 ? void 0 : profile.address) === null || _a === void 0 ? void 0 : _a.city) ? 'distance' : userLocationError ? 'storeName' : 'distance';
        }
    };
    const checkLocationByName = (locationName) => __awaiter(void 0, void 0, void 0, function* () {
        const isPostCode = StoreUtil.isSearchByPostCode(locationName);
        const osmLocation = yield OsmAPI.getLocationFromOSM(isPostCode ? StoreUtil.formatPostCode(locationName, defaultCountryCode) : locationName, isPostCode ? defaultCountryCode : countryCodes);
        const osmLocationByImportance = osmLocation
            .filter(place => LOCATION_TYPE.includes(place.type))
            .sort((a, b) => b.importance - a.importance);
        if (osmLocationByImportance.length > 0) {
            return {
                location: {
                    lat: osmLocationByImportance[0].lat,
                    lng: osmLocationByImportance[0].lon,
                },
                locationName,
                type: LocationSearchType.CUSTOM_SEARCH,
                state: 'SET',
            };
        }
        return null;
    });
    const handleSubmitLocation = (values, setError) => __awaiter(void 0, void 0, void 0, function* () {
        const { location: formLocation } = values;
        if (!formLocation.trim()) {
            return;
        }
        const locationSearchResult = yield checkLocationByName(formLocation);
        if (locationSearchResult) {
            onSearch({
                locationSearch: locationSearchResult,
                isShowStock,
                sortBy: getSortBy(),
                selectedStoreId: userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId,
            });
            setLocationSearchResult(locationSearchResult);
            if (!isShopLanding) {
                CookieUtil.set(Cookie.BROWSER_GEOLOCATION, encodeURIComponent(JSON.stringify(locationSearchResult)), {
                    secure: true,
                });
            }
            LocalStorageUtil.setItem(SEARCH_STORE_STORAGE_KEY, formLocation);
        }
        else {
            setError('location', { type: 'custom', message: t('product.check.search.noLocationFound') });
        }
    });
    const handleOnStoreSelect = (store) => __awaiter(void 0, void 0, void 0, function* () {
        const { storeId, storeName } = store;
        if (typeBasketCheckoutOrCC) {
            yield setClickAndCollectStoreId(storeId);
        }
        LocalStorageUtil.setItem(SELECTED_STORE_STORAGE_KEY, { storeId, storeName });
        onStoreSelect === null || onStoreSelect === void 0 ? void 0 : onStoreSelect(store);
        closeStoreFinderPopup();
    });
    const handleChangeStock = () => {
        setIsShowStock(prevState => !prevState);
        onSearch({
            locationSearch: locationSearchResult,
            isShowStock: !isShowStock,
            sortBy: getSortBy(),
            selectedStoreId: userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId,
        });
    };
    const closeStoreFinderPopup = () => {
        onPopoverClose();
        ZendeskWidgetUtil.resetZIndex();
    };
    const closeStoreFinderPopupCTA = () => setIsStoreFinderPopupCTAOpen(false);
    const handleStoreSelect = (store) => __awaiter(void 0, void 0, void 0, function* () {
        setUserSelectedStore(store);
    });
    const isClickAndCollectCTAVisible = (store) => {
        if (!store) {
            return false;
        }
        const isStoreAvailableOrSSE = Boolean(store.available || store.availableInSSEStore);
        if (!store.selectable && typeBasketCheckoutOrCC) {
            return false;
        }
        if (isSSEProducts) {
            return !isSpecialtyTypeProducts ? isStoreAvailableOrSSE : Boolean(isStoreAvailableOrSSE && store.specialtyStore);
        }
        return isSpecialtyTypeProducts ? Boolean(store.specialtyStore) : true;
    };
    useLockBodyScroll(isOpen);
    if (!isClient)
        return null;
    const getCTAContent = () => (_jsx(ButtonAlpha, { testId: "popup_and_collect", fullWidth: true, theme: "branded", state: (disabledCTA && type !== StoreFinderType.BASKET) ||
            (type === StoreFinderType.IN_STORE_FILTER && !(userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.itemsCount)) ||
            !userSelectedStore
            ? 'disabled'
            : undefined, text: textCTA ||
            (type === StoreFinderType.IN_STORE_FILTER || type === StoreFinderType.BASKET
                ? t(`${I18N_NAMESPACES.product}:product.lister.inStore.choose`)
                : t(`${I18N_NAMESPACES.store}:store.addToBasket`)), onClick: () => handleOnStoreSelect(userSelectedStore) }));
    const showCTAContent = isOpen &&
        isStoreFinderPopupCTAOpen &&
        !!(stores === null || stores === void 0 ? void 0 : stores.length) &&
        isClickAndCollectCTAVisible(userSelectedStore) &&
        Boolean(userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId);
    const getCTASidebar = (props) => showCTAContent ? (_jsx(Sidebar, Object.assign({ isOpen: true, side: "right", align: "bottom", position: "fixed", animation: "none", zIndex: "modal", hasCloseBtn: false, hasBackdrop: false, fullHeight: false, testId: "store-finder-cta-sidebar" }, props, { children: getCTAContent() }))) : null;
    const getContent = () => (_jsxs(Grid, { children: [_jsx(TextAlpha, { as: "h2", variant: "headingS", children: title !== null && title !== void 0 ? title : t('storeFinder.popup.title') }), _jsx(StoreFinderGeneral, { isShowStock: isShowStock, isShowStockCheckbox: typeBasketCheckoutOrCC, valuesForm: {
                    location: locationSearchResult && isLocationSearchResult(locationSearchResult)
                        ? locationSearchResult.locationName
                        : '',
                }, handleSubmitLocation: handleSubmitLocation, handleChangeStock: handleChangeStock, specialtyType: specialtyType, storeFinderType: type, stores: stores }), locationSearchResult.state === 'ERROR' && userLocationStatus !== 'pending' && (_jsx(Banner, { status: "error", text: t('storeFinder.popup.unable.to.geocode') })), storesError && (_jsx(Box, { paddingBlockEnd: "4", children: _jsx(Banner, { text: type === StoreFinderType.BASKET
                        ? t('storeFinder.popup.basket.not.in.stock')
                        : t('storeFinder.popup.not.in.stock'), status: "error" }) })), storesIsLoading && _jsx(StoreListLoader, {}), !!(stores === null || stores === void 0 ? void 0 : stores.length) && !storesIsLoading && (_jsx(StoreList, { stores: stores, storeFinderType: type, sortBy: getSortBy(), location: locationSearchResult && isLocationSearchResult(locationSearchResult)
                    ? locationSearchResult.location
                    : initLocation, isLocationBlocked: isLocationBlocked, handleStoreSelect: handleStoreSelect, userSelectedStoreId: userSelectedStore === null || userSelectedStore === void 0 ? void 0 : userSelectedStore.storeId, selectedStoreInfo: storeInformation, isClickAndCollectCTAVisible: isClickAndCollectCTAVisible }))] }));
    return createPortal(isMobile ? (_jsxs(_Fragment, { children: [_jsx(BottomSheet, { isOpen: isOpen, onClose: closeStoreFinderPopup, padding: "none", testId: "store-finder-bottom-sheet", children: _jsx(Box, { paddingBlockEnd: userSelectedStore ? '14' : undefined, children: getContent() }) }), getCTASidebar({ customWidth: { width: '100%' } })] })) : (_jsx(_Fragment, { children: _jsx(Sidebar, { isOpen: isOpen, onClose: closeStoreFinderPopup, onCloseStart: closeStoreFinderPopupCTA, side: "right", align: "top", position: "fixed", zIndex: "modal", customWidth: { width: SIDEBAR_WIDTH }, hasCloseBtnSpacing: false, footer: {
                content: showCTAContent && getCTAContent(),
                position: 'absolute',
            }, testId: "store-finder-sidebar", children: getContent() }) })), document.body);
};
